/**
 * EchoClient is a WebSocket client for the Echo real-time commenting API used by the Zapworks platform.
 * It allows subscribing to comment updates for a resource, fetching existing comments, and posting new comments.
 */

export interface EchoClientOptions {
  /** Zapworks CMS access token */
  accessToken: string;
  /** CMS environment (e.g. "DEVELOPMENT", "STAGING", "PRODUCTION") */
  cmsEnv: 'DEVELOPMENT' | 'STAGING' | 'PRODUCTION';
  /** The ID of the resource to authorise against */
  resourceId: string;
  /** Optional workspace ID for workspace-scoped assets */
  workspaceId?: string;
  /** Whether to use Echo's development environment (defaults to production) */
  useDev?: boolean;
}

export interface Comment {
  commentId: string;
  resourceId: string;
  userId: string;
  body: string;
  createdAt: number;
  parentCommentId?: string;
  /**
   * Discriminates user-authored comments from server-side activity rows. Absent ⇒ treat as 'user'.
   * System comments carry an event-type key in the form `system:{event-type-key}` so the UI can
   * distinguish, filter, or render different activity types. Legacy `'system'` (no colon) may
   * appear on historical comments and should still be treated as a system row.
   */
  kind?: 'user' | `system:${string}`;
}

export interface ListCommentsOptions {
  resourceId: string;
  limit?: number;
  nextToken?: string;
  rootOnly?: boolean;
  parentCommentId?: string;
}

export interface ListCommentsResult {
  type: 'listCommentsResult';
  resourceId: string;
  items: Comment[];
  nextToken?: string;
}

export interface CommentCreatedEvent {
  type: 'commentCreated';
  resourceId: string;
  comment: Comment;
}

export interface CommentsDeletedEvent {
  type: 'commentsDeleted';
  resourceId: string;
  /** The deleted comment plus any descendants removed by the cascade. */
  commentIds: string[];
}

type ServerMessage = ListCommentsResult | CommentCreatedEvent | CommentsDeletedEvent;

type EventMap = {
  open: () => void;
  close: (event: CloseEvent) => void;
  error: (event: Event) => void;
  listCommentsResult: (data: ListCommentsResult) => void;
  commentCreated: (data: CommentCreatedEvent) => void;
  commentsDeleted: (data: CommentsDeletedEvent) => void;
  message: (data: ServerMessage) => void;
};

type EventName = keyof EventMap;

const ECHO_DOMAIN = 'echo.zap.works';

export class EchoClient {
  private _ws: WebSocket | null = null;
  private _options: EchoClientOptions;
  private _listeners: { [K in EventName]?: Set<EventMap[K]> } = {};
  private _shouldReconnect = true;
  private _reconnectAttempt = 0;
  private _reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly _maxReconnectAttempts = 10;

  constructor(options: EchoClientOptions) {
    this._options = options;
  }

  /** Connect to the WebSocket API. */
  connect(): void {
    if (this._ws?.readyState === WebSocket.OPEN || this._ws?.readyState === WebSocket.CONNECTING) {
      return;
    }
    // A consumer-initiated connect re-enables auto-reconnect after a prior `disconnect()`.
    this._shouldReconnect = true;
    if (this._reconnectTimer !== null) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }

    const { accessToken, cmsEnv, resourceId, workspaceId, useDev } = this._options;
    const base = useDev ? `wss://dev-${ECHO_DOMAIN}` : `wss://${ECHO_DOMAIN}`;

    const params = new URLSearchParams({
      accessToken,
      env: cmsEnv,
      resourceId,
    });

    if (workspaceId) {
      params.set('workspaceId', workspaceId);
    }

    this._ws = new WebSocket(`${base}/?${params.toString()}`);

    this._ws.onopen = () => {
      this._reconnectAttempt = 0;
      this._emit('open');
    };

    this._ws.onclose = (event) => {
      this._emit('close', event);
      this._scheduleReconnect();
    };

    this._ws.onerror = (event) => this._emit('error', event);

    this._ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as ServerMessage;
        this._emit('message', data);

        if (data.type === 'listCommentsResult') {
          this._emit('listCommentsResult', data);
        } else if (data.type === 'commentCreated') {
          this._emit('commentCreated', data);
        } else if (data.type === 'commentsDeleted') {
          this._emit('commentsDeleted', data);
        }
      } catch {
        // Ignore non-JSON messages
      }
    };
  }

  /** Disconnect from the WebSocket API. Cancels any pending auto-reconnect. */
  disconnect(): void {
    this._shouldReconnect = false;
    if (this._reconnectTimer !== null) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
    if (this._ws) {
      this._ws.close();
      this._ws = null;
    }
  }

  /**
   * Schedule an auto-reconnect after the socket closes unexpectedly. Uses exponential
   * backoff capped at 30s; gives up after `_maxReconnectAttempts` and emits an `error`
   * so the UI can surface a "lost connection" state.
   */
  private _scheduleReconnect(): void {
    if (!this._shouldReconnect) return;
    if (this._reconnectTimer !== null) return;

    if (this._reconnectAttempt >= this._maxReconnectAttempts) {
      this._emit('error', new Event('reconnect-gave-up'));
      return;
    }

    const delay = Math.min(30_000, 1000 * 2 ** this._reconnectAttempt);
    this._reconnectAttempt += 1;
    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null;
      // Clear the dead socket so connect()'s readyState early-return passes.
      this._ws = null;
      this.connect();
    }, delay);
  }

  /** Whether the client is currently connected. */
  get connected(): boolean {
    return this._ws?.readyState === WebSocket.OPEN;
  }

  /** Subscribe to real-time comment updates for a resource. */
  subscribe(resourceId: string): void {
    this._send('subscribe', { resourceId });
  }

  /** Unsubscribe from real-time comment updates for a resource. */
  unsubscribe(resourceId: string): void {
    this._send('unsubscribe', { resourceId });
  }

  /** Fetch comments for a resource. Results arrive via the `listCommentsResult` event. */
  listComments(options: ListCommentsOptions): void {
    this._send('listComments', { ...options });
  }

  /** Post a new comment or reply. */
  postComment(
    resourceId: string,
    body: string,
    options?: { parentCommentId?: string; kind?: 'user' | `system:${string}` },
  ): void {
    const payload: Record<string, string> = { resourceId, body };
    if (options?.parentCommentId) {
      payload.parentCommentId = options.parentCommentId;
    }
    if (options?.kind) {
      payload.kind = options.kind;
    }
    this._send('postComment', payload);
  }

  /**
   * Delete a comment owned by the current user. The server cascades to all
   * descendant replies and emits a single `commentsDeleted` event listing
   * every removed commentId. Non-owners receive a `403 Forbidden`.
   */
  deleteComment(resourceId: string, commentId: string): void {
      this._send('deleteComment', { resourceId, commentId });
  }

  /** Register an event listener. */
  on<K extends EventName>(event: K, listener: EventMap[K]): this {
    if (!this._listeners[event]) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (this._listeners as any)[event] = new Set();
    }
    (this._listeners[event] as Set<EventMap[K]>).add(listener);
    return this;
  }

  /** Remove an event listener. */
  off<K extends EventName>(event: K, listener: EventMap[K]): this {
    (this._listeners[event] as Set<EventMap[K]> | undefined)?.delete(listener);
    return this;
  }

  private _send(action: string, payload: Record<string, unknown>): void {
    if (!this._ws || this._ws.readyState !== WebSocket.OPEN) {
      throw new Error('EchoClient is not connected');
    }
    this._ws.send(JSON.stringify({ action, ...payload }));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _emit(event: EventName, ...args: any[]): void {
    const listeners = this._listeners[event];
    if (listeners) {
      listeners.forEach((listener) => {
        (listener as (...a: any[]) => void)(...args);
      });
    }
  }
}
